import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import Button from 'components/Button'
import InfoBanner from 'components/InfoBanner'
import Form from 'components/fields/Form'
import Input from 'components/fields/Input'
import { useToast } from 'hooks/useToast'
import { getPlaceIdFromGoogleMapsUrl } from 'lib/api'

const FormSchema = z.object({
	url: z.string().url(),
})

type FormData = z.infer<typeof FormSchema>

type Props = {
	onChoosePlace: (placeId: string | null) => void
}

function PlaceLink({ onChoosePlace }: Props) {
	const showToast = useToast()
	const { register, handleSubmit, formState, reset } = useForm({
		mode: 'onTouched',
		defaultValues: { url: '' },
		resolver: zodResolver(FormSchema),
	})
	const { isSubmitting } = formState

	async function loadPlaceFromMapsUrl({ url }: FormData) {
		try {
			const { placeId } = await getPlaceIdFromGoogleMapsUrl(url)
			onChoosePlace(placeId)
			reset()
		} catch {
			showToast('error', 'We could not load the place from the provided link')
		}
	}

	return (
		<Form onSubmit={handleSubmit(loadPlaceFromMapsUrl)}>
			<InfoBanner>
				<span>Provide a direct link to your profile on google maps. To find it, go to </span>
				<a href="https://maps.google.com" target="_blank" rel="noopener,noreferrer">
					maps.google.com
				</a>
				<span>, find your business, select share and then “copy link”</span>
			</InfoBanner>

			<Input
				{...register('url')}
				fullFieldsetWidth
				label="Provide link"
				placeholder="Enter Google Maps link"
				error={formState.errors.url?.message}
				button={
					<Button type="submit" kind="primary" disableHover loading={isSubmitting}>
						Add
					</Button>
				}
			/>
		</Form>
	)
}

export default PlaceLink
